import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';

export interface ManualAddressPayload {
  line1: string;
  line2?: string;
  city: string;
  state?: string;
  postalCode?: string;
  countryCode: string;
  label?: string;
}

export interface ManualGeofencePayload {
  center: { lat: number; lng: number };
  radiusMeters: number;
  label?: string;
}

export interface ManualAddressCandidate {
  id: string;
  placeId?: string;
  name?: string;
  formattedAddress?: string;
  coordinates: { lat: number; lng: number };
  distanceMeters?: number;
  source: 'GOOGLE' | 'MANUAL';
}

export interface ManualAddressCandidateResponse {
  data: ManualAddressCandidate[];
  total: number;
  page: number;
  pageSize: number;
}

export interface TerritoryBoundary {
  territoryId: string;
  territoryName: string;
  polygon: Array<{ lat: number; lng: number }>;
  color?: string;
}

export interface LookupParams {
  productIds: string[];
  searchQuery?: string;
  coordinates?: { lat: number; lng: number };
  placeId?: string;
  lookupSource?: 'GOOGLE' | 'MANUAL_ADDRESS' | 'MANUAL_GEOFENCE';
  manualAddress?: ManualAddressPayload;
  manualGeofence?: ManualGeofencePayload;
}

export interface ProductValidationResult {
  productId: string;
  productName: string;
  status: 'CLEAR_TO_APPROACH' | 'NOT_AVAILABLE_IN_TERRITORY' | 'ALREADY_RESERVED' | 'NO_FSR_ACCESS' | 'TERRITORY_NOT_FOUND' | 'ALREADY_IN_CONTRACT';
  statusMessage: string;
  details?: {
    territoryName?: string;
    reservationInfo?: any;
    fsrAccessInfo?: string;
    contractInfo?: any;
  };
}

export interface LookupResult {
  businessName: string;
  formattedAddress: string;
  coordinates: { lat: number; lng: number };
  overallStatus: 'PASS' | 'FAIL';
  overallMessage: string;
  canRegister: boolean;
  canView: boolean;
  customerId?: string;
  placeId?: string;
  lookupSource?: 'GOOGLE' | 'MANUAL_ADDRESS' | 'MANUAL_GEOFENCE';
  manualAddress?: ManualAddressPayload & { normalized?: string };
  manualGeofence?: ManualGeofencePayload;
  territoryInfo?: {
    territoryId: string;
    territoryName: string;
    fsrHasAccess: boolean;
    accessMessage: string;
  };
  productValidations: ProductValidationResult[];
}

export interface BusinessSuggestion {
  place_id: string;
  description: string;
  structured_formatting?: {
    main_text: string;
    secondary_text: string;
  };
}

export interface PlaceDetails {
  place_id: string;
  name?: string;
  formatted_address?: string;
  geometry?: {
    location: { lat: number; lng: number };
  };
  business_status?: string;
  types?: string[];
}

@Injectable({
  providedIn: 'root'
})
export class CustomerLookupService {
  private apiUrl = environment.apiUrl;

  constructor(private http: HttpClient) {}

  /**
   * Perform customer lookup
   */
  performLookup(params: LookupParams): Observable<any> {
    return this.http.post(`${this.apiUrl}/customer-lookup/perform`, params);
  }

  /**
   * Search businesses using Google Places API
   */
  searchBusinesses(query: string): Observable<any> {
    return this.http.get(`${this.apiUrl}/customer-lookup/search-business`, {
      params: { query }
    });
  }

  /**
   * Get place details by place ID
   */
  getPlaceDetails(placeId: string): Observable<any> {
    return this.http.get(`${this.apiUrl}/customer-lookup/place-details/${placeId}`);
  }

  /**
   * Get products assigned to current user (active only)
   */
  getUserAssignedProducts(): Observable<any> {
    return this.http.get(`${this.apiUrl}/customer-lookup/user-products`);
  }

  /**
   * Get cities for a specific country
   */
  getCitiesByCountry(countryCode: string): Observable<any> {
    return this.http.get(`${this.apiUrl}/customer-lookup/cities/${countryCode}`);
  }

  /**
   * Get streets for a specific city in a country
   */
  getStreetsByCity(countryCode: string, city: string): Observable<any> {
    return this.http.get(`${this.apiUrl}/customer-lookup/streets/${countryCode}/${encodeURIComponent(city)}`);
  }

  /**
   * Search for businesses near specific coordinates
   */
  searchNearbyBusinesses(lat: number, lng: number, radius: number = 5000): Observable<any> {
    return this.http.get(`${this.apiUrl}/customer-lookup/nearby-businesses`, {
      params: { lat: lat.toString(), lng: lng.toString(), radius: radius.toString() }
    });
  }

  /**
   * Geocode an address to get coordinates
   */
  geocodeAddress(address: string): Observable<any> {
    return this.http.get(`${this.apiUrl}/customer-lookup/geocode`, {
      params: { address }
    });
  }

  /**
   * Search manual address candidates around provided coordinates
   */
  searchManualAddressCandidates(params: {
    lat: number;
    lng: number;
    radiusMeters?: number;
    page?: number;
    pageSize?: number;
  }): Observable<ManualAddressCandidateResponse> {
    const queryParams: Record<string, string> = {
      lat: params.lat.toString(),
      lng: params.lng.toString(),
      radius: (params.radiusMeters ?? 50).toString(),
      page: (params.page ?? 1).toString(),
      pageSize: (params.pageSize ?? 10).toString()
    };

    return this.http.get<ManualAddressCandidateResponse>(`${this.apiUrl}/customer-lookup/manual-candidates`, {
      params: queryParams
    });
  }

  /**
   * Check for existing customers within the given radius
   */
  checkExistingCustomersInRadius(params: {
    lat: number;
    lng: number;
    radiusMeters: number;
  }): Observable<{ matches: ManualAddressCandidate[] }> {
    const queryParams: Record<string, string> = {
      lat: params.lat.toString(),
      lng: params.lng.toString(),
      radius: params.radiusMeters.toString()
    };

    return this.http.get<{ matches: ManualAddressCandidate[] }>(`${this.apiUrl}/customer-lookup/check-radius`, {
      params: queryParams
    });
  }

  /**
   * Get the territories assigned to the current FSR including polygon data
   */
  getFsrTerritoryBoundaries(): Observable<{ territories: TerritoryBoundary[] }> {
    return this.http.get<{ territories: TerritoryBoundary[] }>(`${this.apiUrl}/customer-lookup/fsr-territories`);
  }

}
